
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const ServicePlans: React.FC = () => {
  return (
    <section className="section-padding bg-gray-50" id="services">
      <div className="container-wide">
        <div className="text-center mb-12">
          <h2 className="heading-lg text-itmgmt-dark-blue mb-4">Residential Service Plans</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choose the right level of support for your home technology needs.
          </p>
        </div>
        
        <Tabs defaultValue="hourly" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="hourly">Hourly Support</TabsTrigger>
            <TabsTrigger value="subscription">Monthly Subscription</TabsTrigger>
          </TabsList>
          
          <TabsContent value="hourly" className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-itmgmt-dark-blue mb-4">Pay-As-You-Go Support</h3>
            <p className="text-gray-600 mb-6">
              Get expert help whenever you need it without any long-term commitment. 
              Our technicians are available for both remote and on-site assistance.
            </p>
            <div className="border-t border-b border-gray-200 py-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">Remote Support:</span>
                <span className="text-itmgmt-dark-blue font-bold">$2.08/min</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">On-Site Support:</span>
                <span className="text-itmgmt-dark-blue font-bold">$125/hour</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              * Minimum 30-minute charge for remote support and 1-hour charge for on-site visits.
            </p>
            <a href="#contact" className="button-primary bg-itmgmt-bright-blue hover:bg-itmgmt-medium-blue text-white px-6 py-3 rounded-md font-medium inline-flex items-center">
              Book a Session
            </a>
          </TabsContent>
          
          <TabsContent value="subscription" className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-itmgmt-dark-blue mb-4">Home Tech Support Plans</h3>
            <p className="text-gray-600 mb-6">
              Ongoing support plans with priority service and regular maintenance for your home technology.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="p-6 bg-itmgmt-dark-blue text-white text-center">
                  <h3 className="text-xl font-bold mb-2">Basic Support</h3>
                  <div className="flex justify-center items-baseline my-4">
                    <span className="text-4xl font-bold">$40</span>
                    <span className="text-lg ml-1">/mo</span>
                  </div>
                  <p className="text-sm text-gray-200">per device</p>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <ul className="space-y-3 mb-6 flex-grow">
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Remote Monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Security Protection</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Monthly PC Health Reports</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Basic Maintenance</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Button asChild className="button-primary w-full">
                      <Link to="/book?service=residential&plan=basic">Get Started</Link>
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden shadow-md relative transform scale-105 z-10 flex flex-col">
                <div className="absolute top-0 right-0 bg-itmgmt-bright-blue text-white py-1 px-3 text-sm">
                  Popular
                </div>
                <div className="p-6 bg-itmgmt-medium-blue text-white text-center">
                  <h3 className="text-xl font-bold mb-2">Full Home Support</h3>
                  <div className="flex justify-center items-baseline my-4">
                    <span className="text-4xl font-bold">$100</span>
                    <span className="text-lg ml-1">/mo</span>
                  </div>
                  <p className="text-sm text-gray-200">additional devices $40/mo each</p>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <ul className="space-y-3 mb-6 flex-grow">
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Everything in Basic Support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Unlimited Remote Support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Priority Response</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Quarterly IT Review</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Button asChild className="button-primary w-full">
                      <Link to="/book?service=residential&plan=full">Get Started</Link>
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="p-6 bg-itmgmt-dark-blue text-white text-center">
                  <h3 className="text-xl font-bold mb-2">Premium Home</h3>
                  <div className="flex justify-center items-baseline my-4">
                    <span className="text-lg">Custom Pricing</span>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <ul className="space-y-3 mb-6 flex-grow">
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Everything in Full Home Support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">On-Site Support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Unlimited Devices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Premium SLA Guarantees</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span className="text-gray-600">Smart Home Integration</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Button asChild className="button-primary w-full">
                      <Link to="/book?service=residential&plan=custom">Contact Us</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ServicePlans;
